/** @fileoverview Shows the files attached to the composer draft as removable status chips. */

import { ImageOff, Loader2, RotateCw, X } from 'lucide-react';
import type { ComposerAttachment } from '../../hooks/useAttachments';
import FileTypeIcon from './FileTypeIcon';

const STATUS_LABEL: Record<ComposerAttachment['status'], string> = {
  preparing: 'Preparing',
  uploading: 'Uploading',
  processing: 'Processing',
  ready: 'Ready',
  failed: 'Failed',
};

/** Ready documents still being embedded read as Indexing; Send waits for it. */
const statusLabel = (item: ComposerAttachment) =>
  item.status === 'ready' && item.indexStatus === 'indexing'
    ? 'Indexing'
    : item.status === 'ready' && item.indexStatus === 'failed'
    ? 'Ready, indexing failed'
    : STATUS_LABEL[item.status];

const buttonClass =
  'flex size-4 items-center justify-center rounded-full border border-app-border bg-app-surface-card text-app-text-muted shadow-sm hover:text-app-text';

function ImageChip({
  item,
  onRemove,
  onRetry,
}: {
  item: ComposerAttachment;
  onRemove?: (key: string) => void;
  onRetry?: (key: string) => void;
}) {
  const busy = item.status === 'preparing' || item.status === 'uploading';
  return (
    <li
      className={`relative size-14 overflow-hidden rounded-lg border ${
        item.status === 'failed' ? 'border-app-danger' : 'border-app-border'
      }`}
      title={item.error}
    >
      <img
        alt="Attached"
        className={`size-full object-cover ${item.status === 'ready' ? '' : 'opacity-50'}`}
        src={item.previewUrl}
      />
      {busy && (
        <span
          className="absolute inset-0 flex items-center justify-center"
          role="status"
          aria-label={`${statusLabel(item)} image`}
        >
          <Loader2 className="h-4 w-4 animate-spin text-app-text" aria-hidden="true" />
        </span>
      )}
      {item.status === 'failed' && !item.final && (
        <button
          type="button"
          className="absolute inset-0 flex items-center justify-center"
          aria-label="Retry upload"
          title={`${item.error ?? 'Upload failed'} Click to retry.`}
          onClick={() => onRetry?.(item.key)}
        >
          <ImageOff className="h-4 w-4 text-app-danger" aria-hidden="true" />
        </button>
      )}
      <button
        type="button"
        className={`absolute right-0.5 top-0.5 ${buttonClass}`}
        aria-label="Remove image"
        title="Remove image"
        onClick={() => onRemove?.(item.key)}
      >
        <X className="h-2.5 w-2.5" aria-hidden="true" />
      </button>
    </li>
  );
}

function DocumentChip({
  item,
  onRemove,
  onRetry,
}: {
  item: ComposerAttachment;
  onRemove?: (key: string) => void;
  onRetry?: (key: string) => void;
}) {
  const failed = item.status === 'failed';
  const working = item.status !== 'ready' && !failed;
  const indexing = item.status === 'ready' && item.indexStatus === 'indexing';
  return (
    <li
      className={`flex h-14 max-w-56 min-w-0 items-center gap-2 rounded-lg border bg-app-surface-muted px-2 text-xs ${
        failed ? 'border-app-danger' : 'border-app-border'
      }`}
    >
      <FileTypeIcon name={item.name} className="h-5 w-5 shrink-0 text-app-text-muted" />
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-app-text" title={item.name}>
          {item.name}
        </span>
        <span
          className={`flex items-center gap-1 truncate ${
            failed ? 'text-app-danger' : 'text-app-text-muted'
          }`}
          role="status"
          title={item.error}
        >
          {(working || indexing) && (
            <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden="true" />
          )}
          {failed ? item.error ?? 'Failed' : statusLabel(item)}
        </span>
      </span>
      {failed && !item.final && (
        <button
          type="button"
          className={`shrink-0 ${buttonClass}`}
          aria-label={`Retry ${item.name}`}
          title="Retry"
          onClick={() => onRetry?.(item.key)}
        >
          <RotateCw className="h-2.5 w-2.5" aria-hidden="true" />
        </button>
      )}
      <button
        type="button"
        className={`shrink-0 ${buttonClass}`}
        aria-label={`Remove ${item.name}`}
        title="Remove"
        onClick={() => onRemove?.(item.key)}
      >
        <X className="h-2.5 w-2.5" aria-hidden="true" />
      </button>
    </li>
  );
}

export default function AttachmentChips({
  attachments,
  onRemove,
  onRetry,
}: {
  attachments: ComposerAttachment[];
  onRemove?: (key: string) => void;
  onRetry?: (key: string) => void;
}) {
  return (
    <ul className="flex flex-wrap gap-2" aria-label="Attached files">
      {attachments.map((item) => {
        const Chip = item.kind === 'image' ? ImageChip : DocumentChip;
        return <Chip item={item} key={item.key} onRemove={onRemove} onRetry={onRetry} />;
      })}
    </ul>
  );
}
